'use client'

import * as React from 'react'

import { useInfiniteQuery, useQuery } from '@tanstack/react-query'
import { AxiosError } from 'axios'

import {
  createDisabledInfiniteQuery,
  createDisabledRegularQuery,
  extractQueryItems,
  flattenInfinitePages,
  resolveInfiniteQueryOptions,
  resolveRegularQueryOptions
} from '@/utils/query'

import type { QueryKey } from '@/query-client/query'
import type {
  DynamicQueryOptions,
  InfiniteQueryOptions,
  RegularQueryOptions
} from '@/types/query'

// ============================================================================
// Types
// ============================================================================

export type UseDynamicQueryParams<T extends object = object> = {
  /**
   * Selected value id(s) not yet present in the loaded page/search results.
   * When provided (non-empty), the query is resolved by id instead of by search —
   * used to fetch the label(s) of a Combobox/MultiCombobox initial value that isn't
   * in the first page of results.
   */
  ids?: Array<number | string>
  /**
   * Whether to use infinite query mode (for pagination/infinite scroll)
   */
  infinite?: boolean
  /**
   * Whether the query is in static mode (using local options array instead of API)
   * When true, queries are disabled and local data is used
   */
  isStaticMode: boolean
  /**
   * Dynamic query options - can be a query options object or a function that receives search params
   */
  queryOptions?: DynamicQueryOptions<T>
  /**
   * Current search query string for server-side filtering
   */
  searchQuery: string
}

export type UseDynamicQueryResult<T extends object = object> = {
  /**
   * Fetch next page (infinite query only)
   */
  fetchNextPage: (() => void) | undefined
  /**
   * Whether there's a next page available (infinite query only)
   */
  hasNextPage: boolean
  /**
   * Whether fetching next page (infinite query only)
   */
  isFetchingNextPage: boolean
  /**
   * Whether the initial query is loading
   */
  isLoading: boolean
  /**
   * Flattened array of items from all pages (infinite) or single page (regular)
   */
  items: T[]
  /**
   * Resolved infinite query options (if infinite mode)
   */
  resolvedInfiniteQueryOptions: InfiniteQueryOptions<T> | undefined
  /**
   * Resolved regular query options (if regular mode)
   */
  resolvedRegularQueryOptions: RegularQueryOptions<T> | undefined
}

// ============================================================================
// Hook: useDynamicQuery
// ============================================================================

/**
 * A comprehensive hook for handling dynamic query options with support for:
 * - Regular queries (useQuery)
 * - Infinite queries (useInfiniteQuery)
 * - Static mode (local options without API calls)
 * - Dynamic query options (function or direct object)
 * - Search query integration
 *
 * @example
 * // Regular query mode
 * const { items, isLoading } = useDynamicQuery({
 *   queryOptions: getAllUsersQuery,
 *   searchQuery,
 *   isStaticMode: false
 * })
 *
 * @example
 * // Infinite query mode
 * const { items, isLoading, hasNextPage, fetchNextPage } = useDynamicQuery({
 *   queryOptions: getAllUsersInfiniteQuery,
 *   searchQuery,
 *   isStaticMode: false,
 *   infinite: true
 * })
 *
 * @example
 * // Static mode (local options)
 * const { items } = useDynamicQuery({
 *   isStaticMode: true,
 *   searchQuery: ''
 * })
 */
export function useDynamicQuery<T extends object = object>(
  params: UseDynamicQueryParams<T>
): UseDynamicQueryResult<T> {
  const {
    ids,
    infinite = false,
    isStaticMode,
    queryOptions,
    searchQuery
  } = params

  // Dedupe + sort ids into a stable key so array-identity churn from the
  // caller doesn't cause needless refetches/query-key changes
  const idsKey = React.useMemo(
    () =>
      ids && ids.length > 0
        ? Array.from(new Set(ids.map(String)))
            .sort()
            .join(',')
        : '',
    [ids]
  )

  // Resolve query options (function or direct)
  const resolvedRegularQueryOptions = React.useMemo(
    () =>
      resolveRegularQueryOptions<T>({
        ids,
        infinite,
        isStaticMode,
        queryOptions,
        searchQuery
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [queryOptions, infinite, isStaticMode, searchQuery, idsKey]
  )

  const resolvedInfiniteQueryOptions = React.useMemo(
    () =>
      resolveInfiniteQueryOptions<T>({
        ids,
        infinite,
        isStaticMode,
        queryOptions,
        searchQuery
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [queryOptions, infinite, isStaticMode, searchQuery, idsKey]
  )

  // Generate query keys with search query / ids included for proper cache separation
  const regularQueryKey = React.useMemo<QueryKey>(
    () =>
      resolvedRegularQueryOptions
        ? ([
            ...resolvedRegularQueryOptions.queryKey,
            searchQuery,
            idsKey
          ] as QueryKey)
        : (['static-options', searchQuery, idsKey] as QueryKey),
    [resolvedRegularQueryOptions, searchQuery, idsKey]
  )

  const infiniteQueryKey = React.useMemo<QueryKey>(() => {
    const key: QueryKey = resolvedInfiniteQueryOptions
      ? ([
          ...(resolvedInfiniteQueryOptions.queryKey as QueryKey),
          'infinite',
          searchQuery,
          idsKey
        ] as QueryKey)
      : ['static-options-infinite', searchQuery, idsKey]

    return key
  }, [resolvedInfiniteQueryOptions, searchQuery, idsKey])

  // -------------------------------------------------------------------------
  // Regular Query
  // -------------------------------------------------------------------------
  const regularQuery = useQuery(
    isStaticMode || !resolvedRegularQueryOptions
      ? createDisabledRegularQuery<T>(regularQueryKey)
      : ({
          ...resolvedRegularQueryOptions,
          enabled:
            !infinite &&
            !isStaticMode &&
            (resolvedRegularQueryOptions.enabled ?? true),
          queryKey: regularQueryKey
        } as Parameters<
          typeof useQuery<unknown, AxiosError<API>, unknown, QueryKey>
        >[0])
  )

  // -------------------------------------------------------------------------
  // Infinite Query
  // -------------------------------------------------------------------------
  const infiniteQueryEnabled = Boolean(
    infinite &&
      !isStaticMode &&
      (typeof resolvedInfiniteQueryOptions?.enabled === 'boolean'
        ? resolvedInfiniteQueryOptions.enabled
        : true)
  )
  const infiniteQuery = useInfiniteQuery(
    isStaticMode || !resolvedInfiniteQueryOptions
      ? createDisabledInfiniteQuery<T>(infiniteQueryKey)
      : ({
          ...resolvedInfiniteQueryOptions,
          enabled: infiniteQueryEnabled,
          queryKey: infiniteQueryKey
        } as InfiniteQueryOptions<T>)
  )

  // -------------------------------------------------------------------------
  // Extract Items
  // -------------------------------------------------------------------------
  const items = React.useMemo<T[]>(() => {
    if (isStaticMode) return []

    if (infinite) {
      return flattenInfinitePages<T>(infiniteQuery.data)
    }

    return extractQueryItems<T>(regularQuery.data)
  }, [isStaticMode, infinite, infiniteQuery.data, regularQuery.data])

  // -------------------------------------------------------------------------
  // Derived State
  // -------------------------------------------------------------------------
  const isLoading = isStaticMode
    ? false
    : infinite
      ? infiniteQuery.isLoading
      : regularQuery.isLoading

  const isFetchingNextPage = infinite ? infiniteQuery.isFetchingNextPage : false

  const hasNextPage = infinite ? (infiniteQuery.hasNextPage ?? false) : false

  const fetchNextPage = infinite ? infiniteQuery.fetchNextPage : undefined

  return {
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    items,
    resolvedInfiniteQueryOptions,
    resolvedRegularQueryOptions
  }
}
